"use client";

import { c as _c } from "react/compiler-runtime";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@repo/design-system/components/ui/drawer";
import { cn } from "@repo/design-system/utils/cn";
import dynamic from "next/dynamic";
import { type ComponentType, useEffect, useState } from "react";
import { useWindowSize } from "usehooks-ts";
import { SVGNewsletter } from "~/components/icons/newsletter-eon";
import { Button, type ButtonProps } from "~/components/ui/button-eon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog-eon";
import { InfoIcon } from "~/components/ui/info-icon-eon";
import { env } from "~/setup/env";
import { useLanguage } from "~/translations/loader/use-language";
import { NewsletterSearchSummary } from "./newsletter-search-summary-eon";

// Dynamic imports for newsletter components
const NewsletterFilterbar = dynamic(() => import("./newsletter-filterbar-eon").then(mod => mod.NewsletterFilterbar), {
  loading: () => <div className="h-[300px] animate-pulse rounded bg-muted" />
});
const NewsletterForm = dynamic(() => import("./newsletter-form-eon"), {
  loading: () => <div className="h-[400px] animate-pulse rounded bg-muted" />
});

// Preload function to load components on hover
const preloadNewsletterComponents = () => {
  import("./newsletter-filterbar-eon");
  import("./newsletter-form-eon");
};
type BaseProps<T> = {
  title?: string;
  className?: string;
} & T;
interface BaseNewsletterModalProps {
  filterbarComponent?: ComponentType;
  onNewsletterBtnClick?: () => void;
}
type NewsletterModalProps = (BaseProps<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> & BaseNewsletterModalProps & {
  variant?: ButtonProps["variant"];
}) | (BaseProps<{
  open?: undefined;
  onOpenChange?: undefined;
}> & BaseNewsletterModalProps & {
  variant?: ButtonProps["variant"];
});
export function NewsletterModal(t0) {
  const $ = _c(70);
  const {
    title,
    className,
    open,
    onOpenChange,
    filterbarComponent,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "ghost" : t1;
  const t = useLanguage();
  const {
    width
  } = useWindowSize();
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMounted(true);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  const isMobile = mounted && width <= 768;
  const isControlled = Boolean(onOpenChange);
  if (isMobile) {
    const t4 = isControlled && "hidden";
    let t5;
    if ($[2] !== t4) {
      t5 = cn(t4);
      $[2] = t4;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== className) {
      t6 = cn("w-full justify-between", className);
      $[4] = className;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== t.filterbar_subscribe || $[7] !== t.filterbar_subscribe_info || $[8] !== title) {
      t7 = title ? title : <div className="flex w-full items-center justify-between"><div /><div className="flex items-center gap-x-2"><span className="px-2" data-source="lc">{t.filterbar_subscribe}</span><InfoIcon>{t.filterbar_subscribe_info}</InfoIcon></div><SVGNewsletter width={24} /></div>;
      $[6] = t.filterbar_subscribe;
      $[7] = t.filterbar_subscribe_info;
      $[8] = title;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t6 || $[11] !== t7 || $[12] !== variant) {
      t8 = <Button className={t6} onMouseEnter={preloadNewsletterComponents} variant={variant}>{t7}</Button>;
      $[10] = t6;
      $[11] = t7;
      $[12] = variant;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t5 || $[15] !== t8) {
      t9 = <DrawerTrigger asChild={true} className={t5}>{t8}</DrawerTrigger>;
      $[14] = t5;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        backgroundImage: `url(${env.NEXT_PUBLIC_ASSET_URL}images/jobboard/filterbar_pattern_small-compressed.webp)`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover"
      };
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== t.newsletter_modal_header) {
      t11 = <div className="shrink-0 border-white/30 border-b p-2"><DrawerHeader><DrawerTitle className="font-medium text-2xl text-white">{t.newsletter_modal_header}</DrawerTitle></DrawerHeader></div>;
      $[18] = t.newsletter_modal_header;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== filterbarComponent) {
      t12 = <NewsletterContent filterbarComponent={filterbarComponent} />;
      $[20] = filterbarComponent;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <NewsletterForm />;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t12) {
      t14 = <div className="hide-scrollbar flex-1 overflow-y-auto px-6 pb-6 md:px-8 md:pb-8">{t12}{t13}</div>;
      $[23] = t12;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== t11 || $[26] !== t14) {
      t15 = <DrawerContent className="h-[80%] text-white lg:h-80" style={t10}><div className="flex flex-1 flex-col overflow-hidden">{t11}{t14}</div></DrawerContent>;
      $[25] = t11;
      $[26] = t14;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== onOpenChange || $[29] !== open || $[30] !== t15 || $[31] !== t9) {
      t16 = <Drawer onOpenChange={onOpenChange} open={open}>{t9}{t15}</Drawer>;
      $[28] = onOpenChange;
      $[29] = open;
      $[30] = t15;
      $[31] = t9;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    return t16;
  }
  const t4 = isControlled && "hidden";
  let t5;
  if ($[33] !== t4) {
    t5 = cn(t4);
    $[33] = t4;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  let t6;
  if ($[35] !== className) {
    t6 = cn("w-full justify-between", className);
    $[35] = className;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div />;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== t.filterbar_subscribe || $[39] !== t.filterbar_subscribe_info || $[40] !== title) {
    t8 = title ? title : <div className="flex w-full items-center justify-between"><div className="flex items-center gap-x-2"><span className="px-2" data-source="lc">{t.filterbar_subscribe}</span><InfoIcon>{t.filterbar_subscribe_info}</InfoIcon></div><SVGNewsletter className="size-6" /></div>;
    $[38] = t.filterbar_subscribe;
    $[39] = t.filterbar_subscribe_info;
    $[40] = title;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== t6 || $[43] !== t8 || $[44] !== variant) {
    t9 = <Button className={t6} onMouseEnter={preloadNewsletterComponents} variant={variant}>{t7}{t8}</Button>;
    $[42] = t6;
    $[43] = t8;
    $[44] = variant;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== t5 || $[47] !== t9) {
    t10 = <DialogTrigger asChild={true} className={t5}>{t9}</DialogTrigger>;
    $[46] = t5;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      backgroundImage: `url(${env.NEXT_PUBLIC_ASSET_URL}images/jobboard/filterbar_pattern_small-compressed.webp)`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover"
    };
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t.newsletter_modal_header) {
    t12 = <DialogTitle className="font-medium text-2xl md:mb-4">{t.newsletter_modal_header}</DialogTitle>;
    $[50] = t.newsletter_modal_header;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== t.newsletter_modal_intro) {
    t13 = <p data-source="lc">{t.newsletter_modal_intro}</p>;
    $[52] = t.newsletter_modal_intro;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== t12 || $[55] !== t13) {
    t14 = <div className="shrink-0 border-white/30 border-b p-6 md:px-8"><DialogHeader>{t12}{t13}</DialogHeader></div>;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== filterbarComponent) {
    t15 = <NewsletterContent filterbarComponent={filterbarComponent} />;
    $[57] = filterbarComponent;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <NewsletterForm />;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t15) {
    t17 = <div className="hide-scrollbar flex-1 overflow-y-auto px-8 pb-8 md:p-8">{t15}{t16}</div>;
    $[60] = t15;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t14 || $[63] !== t17) {
    t18 = <DialogContent className="flex max-h-[80vh] flex-col rounded-lg p-0 text-white sm:max-w-3xl" onEscapeKeyDown={_temp} onPointerDownOutside={_temp2} style={t11}><div className="flex flex-1 flex-col overflow-hidden">{t14}{t17}</div></DialogContent>;
    $[62] = t14;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== onOpenChange || $[66] !== open || $[67] !== t10 || $[68] !== t18) {
    t19 = <Dialog onOpenChange={onOpenChange} open={open}>{t10}{t18}</Dialog>;
    $[65] = onOpenChange;
    $[66] = open;
    $[67] = t10;
    $[68] = t18;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  return t19;
}
function _temp2(e_0) {
  if (document.querySelector("[data-slot=\"combobox-content\"]") !== null) {
    e_0.preventDefault();
  }
}
function _temp(e) {
  if (document.querySelector("[data-slot=\"combobox-content\"]") !== null) {
    e.preventDefault();
  }
}
function NewsletterContent(t0) {
  const $ = _c(5);
  const {
    filterbarComponent: t1
  } = t0;
  const FilterbarComponent = t1 === undefined ? NewsletterFilterbar : t1;
  let t2;
  if ($[0] !== FilterbarComponent) {
    t2 = <FilterbarComponent />;
    $[0] = FilterbarComponent;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <NewsletterSearchSummary />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <>{t2}{t3}</>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}